// Avoid circular dependency by importing from common-utils/src/Money
import { round2dp } from '@tix/common-utils/src/Money';
/**
 * Maps each item to `format(item, suffix)`. `suffix` disambiguates cart fees that share a name:
 * the event name when the name repeats across event names, `Event / Group` when it also repeats under one event name,
 * or `''` when the name is unique. Parts with missing names are omitted.
 *
 * Duplicated in ticketing-cms/resources/assets/js/components/cart/cartFees.js. Keep both copies in sync.
 */
export function cartFeeLabelSuffixes(items, format) {
    // NUL (`\0`) separates the event name from the fee name. A printable character such as "-"
    // would make "Event A" + "B-C" the same key as "Event A-B" + "C".
    const withEventKey = (item) => `${item.eventTemplateName}\0${item.name}`;
    const nameCounts = new Map();
    const nameCountsByEvent = new Map();
    const addType = (map, key, typeId) => {
        if (!map.has(key)) {
            map.set(key, new Set());
        }
        map.get(key).add(typeId);
    };
    for (const item of items) {
        addType(nameCounts, item.name, item.ticketTypeId);
        addType(nameCountsByEvent, withEventKey(item), item.ticketTypeId);
    }
    return items.map((item) => {
        const duplicate = (nameCounts.get(item.name)?.size ?? 0) > 1;
        const duplicateInEvent = (nameCountsByEvent.get(withEventKey(item))?.size ?? 0) > 1;
        const { eventTemplateName, ticketGroupName } = item;
        let suffix = undefined;
        if (duplicate) {
            suffix = eventTemplateName;
        }
        if (duplicateInEvent) {
            suffix += ` / ${ticketGroupName}`;
        }
        return format(item, suffix);
    });
}
// Duplicated in ticketing-cms/resources/assets/js/components/cart/cartFees.js. Keep both copies in sync.
export function deduplicateCartFeeLineLabels(ticketFeeLines) {
    return cartFeeLabelSuffixes(ticketFeeLines.map((line) => ({
        line,
        name: line.label,
        ticketTypeId: line.ticketTypeId,
        eventTemplateName: line.eventTemplateName,
        ticketGroupName: line.ticketGroupTypeName,
    })), ({ line }, suffix) => ({
        ...line,
        label: suffix ? `${line.label} (${suffix})` : line.label,
    }));
}
/**
 * TODO: Replace the three arguments with a single Cart entity containing the three entities
 * when web-client is migrated to Vue 3.
 *
 * Duplicated in ticketing-cms/resources/assets/js/components/cart/cartFees.js. Keep both copies in sync except `removable` field.
 */
export function cartFeeLines(tickets, ticketGroups, ticketTypes, eventTemplates) {
    const groupsById = new Map(ticketGroups.map((group) => [group.id, group]));
    const typesById = new Map(ticketTypes.map((type) => [type.id, type]));
    const templatesById = new Map(eventTemplates.map((template) => [template.id, template]));
    const collected = new Map();
    for (const ticket of tickets) {
        const group = groupsById.get(ticket.ticket_group_id);
        if (!group) {
            return {
                data: null,
                error: new Error(`Fee ticket ${ticket.id} is missing its ticket group ${ticket.ticket_group_id}`),
            };
        }
        if (group.handler !== 'fee')
            continue;
        const type = typesById.get(ticket.ticket_type_id);
        if (!type) {
            return {
                data: null,
                error: new Error(`Fee ticket ${ticket.id} in group ${ticket.ticket_group_id} references missing ticket type ${ticket.ticket_type_id}`),
            };
        }
        const existing = collected.get(type.id);
        const parsed = Number(ticket.face_value);
        const amount = Number.isFinite(parsed) ? parsed : 0;
        if (existing) {
            existing.amount = round2dp(existing.amount + amount);
            existing.ticketIds.push(ticket.id);
        }
        else {
            collected.set(type.id, {
                ticketTypeId: type.id,
                ticketGroupId: ticket.ticket_group_id,
                eventTemplateId: ticket.event_template_id,
                label: type.name,
                ticketTypeName: type.name,
                ticketGroupTypeName: groupsById.get(ticket.ticket_group_id).name,
                eventTemplateName: templatesById.get(ticket.event_template_id).name,
                ticketIds: [ticket.id],
                amount,
            });
        }
    }
    const deduped = deduplicateCartFeeLineLabels(Array.from(collected.values()));
    const data = deduped.sort((a, b) => a.label.localeCompare(b.label));
    return { data, error: null };
}
